/**
 * パスワードログインの流れ（指示書 A2 の A-1②部分）。画面（Phase2Login.vue）から状態を切り離して、テストできるようにする。
 *
 *   - 認証要求は同時に1つだけ。実行中にもう一度押されても、新しい要求を重ねない（A-03）。
 *   - 固定の待機（1秒）はしない。認証が終わったら、すぐに遷移し、遷移の結果まで扱う（A-02）。
 *   - 認証の待機には期限がある。期限を過ぎても、裏の認証要求は止まらない（SDKに中止の手段がない）ので、
 *     その結果を捨てずに待ち、遅れて成功したら、まだログイン画面にいる場合だけ1回だけ続行する（A-05）。
 *     終わらない場合は、画面に「再読み込み」を案内する。自動で再認証・再読み込みはしない（A-06）。
 *   - 遷移（画面の読み込みを含む）にも期限がある。期限を過ぎたら待機を解き、「もう一度進む」と「再読み込み」を案内する。
 *     遅れて遷移が終わったら、その結果を1回だけ知らせる。やり直したあとは、古い遷移の結果は無視する（レビュー指摘2）。
 *   - 認証が終わった時点で、ログイン画面を離れていたら、遷移しない（期限内に終わった場合も同じ。レビュー指摘3）。
 *   - 遷移が中断・失敗したときは、パスワードを送り直さずに、遷移だけをやり直せる。
 *   - 遷移の責任者は、この流れ1つだけ（認証通知 SIGNED_IN からは遷移しない）。
 */

export const LOGIN_AUTH_DEADLINE_MS = 15000
/** ルーターのガードの期限（15秒）より長くする。ガードが回復画面へ出す余裕を残すため */
export const LOGIN_NAVIGATION_DEADLINE_MS = 20000

/** vue-router の NavigationFailureType: aborted=4, cancelled=8, duplicated=16 */
export function navigationFailureTypeName(failure) {
  if (!failure) return null
  const t = Number(failure.type)
  if (t === 4) return 'aborted'
  if (t === 8) return 'cancelled'
  if (t === 16) return 'duplicated'
  return 'unknown'
}

/**
 * @param {{
 *   signIn: (email: string, password: string) => Promise<{ data?: any, error?: any }>,
 *   navigate: (path: string) => Promise<any>,
 *   isStillOnLogin: () => boolean,
 *   onAuthenticated?: (user: any) => void,
 *   onLateResult?: (result: object) => void,
 *   authDeadlineMs?: number,
 *   navigationDeadlineMs?: number,
 *   setTimeoutFn?: typeof setTimeout,
 *   clearTimeoutFn?: typeof clearTimeout
 * }} deps
 */
export function createLoginFlow(deps) {
  const {
    signIn,
    navigate,
    isStillOnLogin,
    onAuthenticated = () => {},
    onLateResult = () => {},
    authDeadlineMs = LOGIN_AUTH_DEADLINE_MS,
    navigationDeadlineMs = LOGIN_NAVIGATION_DEADLINE_MS,
    setTimeoutFn = setTimeout,
    clearTimeoutFn = clearTimeout
  } = deps

  const state = {
    busy: false,
    /** 期限を過ぎても、まだ結果が出ていない認証要求 */
    pendingAuth: null,
    /** 遷移だけをやり直すための行き先（認証済みのとき） */
    retryPath: null,
    /** 何回目の遷移か（やり直したら、古い遷移の結果を無視するため） */
    navGen: 0,
    /** 期限を過ぎても、まだ終わっていない遷移があるか */
    navigationPending: false
  }

  const callSafely = (fn, ...args) => {
    try { fn(...args) } catch (_) { /* 画面側の表示処理の失敗で、流れを止めない */ }
  }

  /** 期限内に終わったか、期限を過ぎたかを返す（元のPromiseは止めない） */
  function waitWithDeadline(promise, ms) {
    return new Promise((resolve) => {
      let done = false
      const timer = setTimeoutFn(() => {
        if (done) return
        done = true
        resolve({ timedOut: true })
      }, ms)
      promise.then(
        (value) => {
          if (done) return
          done = true
          clearTimeoutFn(timer)
          resolve({ timedOut: false, value })
        },
        (error) => {
          if (done) return
          done = true
          clearTimeoutFn(timer)
          resolve({ timedOut: false, error })
        }
      )
    })
  }

  const toNavigationResult = (failure) => {
    const type = navigationFailureTypeName(failure)
    if (!type || type === 'duplicated') return { kind: 'navigated' }
    return { kind: 'navigation_failed', failureType: type }
  }

  async function goTo(path) {
    state.retryPath = path
    const gen = ++state.navGen
    state.navigationPending = false
    let navPromise
    try {
      navPromise = Promise.resolve(navigate(path))
    } catch (error) {
      return { kind: 'navigation_failed', failureType: 'error', error }
    }
    const settled = navPromise.then(
      (failure) => toNavigationResult(failure),
      (error) => ({ kind: 'navigation_failed', failureType: 'error', error })
    )
    const r = await waitWithDeadline(settled, navigationDeadlineMs)
    if (!r.timedOut) {
      if (gen !== state.navGen) return { kind: 'superseded' }
      if (r.value.kind === 'navigated') state.retryPath = null
      return r.value
    }
    // 遷移が期限内に終わらない（画面の読み込みが止まった等）。待機を解き、やり直し・再読み込みを案内する。
    // 遅れて終わったら1回だけ知らせる。やり直し後（gen が進んだ後）の古い結果は無視する
    state.navigationPending = true
    settled.then((result) => {
      if (gen !== state.navGen) return
      state.navigationPending = false
      if (result.kind === 'navigated') state.retryPath = null
      callSafely(onLateResult, { ...result, late: true })
    })
    return { kind: 'navigation_waiting' }
  }

  async function continueAfterAuth(user, nextPath) {
    callSafely(onAuthenticated, user)
    // 認証が終わった時点でログイン画面を離れていたら、遷移しない（別の画面にいる人を勝手に移動させない）
    if (!isStillOnLogin()) {
      state.retryPath = null
      return { kind: 'left_login_screen' }
    }
    return goTo(nextPath)
  }

  /** 期限を過ぎた認証要求の結果を待ち、出たら1回だけ処理する */
  function followLateAuth(authPromise, nextPath) {
    authPromise.then(
      async (value) => {
        if (state.pendingAuth !== authPromise) return
        state.pendingAuth = null
        const user = value?.data?.user
        if (value?.error || !user) {
          state.busy = false
          callSafely(onLateResult, { kind: 'auth_error', error: value?.error || new Error('ログインに失敗しました') })
          return
        }
        const result = await continueAfterAuth(user, nextPath)
        state.busy = false
        callSafely(onLateResult, { ...result, late: true })
      },
      (error) => {
        if (state.pendingAuth !== authPromise) return
        state.pendingAuth = null
        state.busy = false
        callSafely(onLateResult, { kind: 'auth_error', error })
      }
    )
  }

  /**
   * ログインする。
   * @returns {Promise<{ kind: 'busy'|'auth_error'|'auth_waiting'|'navigated'|'navigation_failed'|'navigation_waiting'|'left_login_screen', error?: any, failureType?: string }>}
   */
  async function submit({ email, password, nextPath }) {
    if (state.busy) {
      return { kind: 'busy', waitingForPreviousAuth: Boolean(state.pendingAuth), navigationPending: state.navigationPending }
    }
    if (state.navigationPending) {
      // 認証済みで、遷移の終わりを待っている。パスワードを送り直さず、遷移のやり直しを案内する
      return { kind: 'busy', waitingForPreviousAuth: false, navigationPending: true }
    }
    state.busy = true
    state.retryPath = null
    let authPromise
    try {
      authPromise = Promise.resolve(signIn(String(email || '').trim(), password))
    } catch (error) {
      state.busy = false
      return { kind: 'auth_error', error }
    }
    const r = await waitWithDeadline(authPromise, authDeadlineMs)
    if (r.timedOut) {
      // 裏の要求は続いている。新しい要求は重ねず、結果を待つ
      state.pendingAuth = authPromise
      followLateAuth(authPromise, nextPath)
      return { kind: 'auth_waiting' }
    }
    if (r.error || r.value?.error) {
      state.busy = false
      return { kind: 'auth_error', error: r.error || r.value.error }
    }
    const user = r.value?.data?.user
    if (!user) {
      state.busy = false
      return { kind: 'auth_error', error: new Error('ログインに失敗しました') }
    }
    const result = await continueAfterAuth(user, nextPath)
    state.busy = false
    return result
  }

  /** 認証済みで、遷移だけが失敗・停止したとき。パスワードは送り直さない */
  async function retryNavigation() {
    if (state.busy || !state.retryPath) return { kind: 'busy' }
    state.busy = true
    const result = await goTo(state.retryPath)
    state.busy = false
    return result
  }

  return {
    submit,
    retryNavigation,
    get busy() { return state.busy },
    get waitingForPreviousAuth() { return Boolean(state.pendingAuth) },
    get navigationPending() { return state.navigationPending },
    get canRetryNavigation() { return Boolean(state.retryPath) && !state.busy }
  }
}
